import { useState } from 'react';
import { providerLabel, retryAuthBoot, signInWith, signInWithEmail, verifyEmailCode } from '../lib/auth.js';
import { useAuth } from '../lib/hooks.js';
import { Spinner } from './ui.jsx';
import Landing from '../pages/Landing.jsx';

/**
 * The sign-in gate.
 *
 * Wraps the whole app rather than each screen: every route reads pantry data,
 * so there is no useful signed-out view of it to fall back to. The app shell
 * itself stays public — a signed-out browser has to be able to load the page it
 * signs in from, which is why the server gates `/api/` alone.
 *
 * The signed-out state is the landing page, with the provider buttons on it,
 * rather than a bare card: this is the only public URL the product has, so it
 * is what a first-time visitor and anyone reviewing the domain actually sees.
 * The three states below it are failures, and stay plain.
 *
 * Locally, where no Supabase project is configured, the server reports auth as
 * not required and this renders nothing at all.
 */
export default function SignInGate({ children }) {
  const auth = useAuth();

  if (auth.status === 'off' || auth.status === 'signed-in') return children;

  if (auth.status === 'loading') {
    return (
      <Screen>
        <div className="row center"><Spinner /> <span className="muted">Starting up…</span></div>
      </Screen>
    );
  }

  if (auth.status === 'unreachable') {
    return (
      <Screen title="Can't reach the server">
        <p className="small muted">{auth.message}</p>
        <button type="button" className="primary full" onClick={() => retryAuthBoot()}>
          Try again
        </button>
      </Screen>
    );
  }

  if (auth.status === 'unconfigured') {
    return (
      <Screen title="Sign-in isn't set up">
        <div className="alert error" role="alert">{auth.message}</div>
        <p className="tiny muted">
          The server requires a signed-in user but did not report a Supabase project to sign in
          against. This is a deployment setting, not something to fix from here.
        </p>
      </Screen>
    );
  }

  return <SignedOut auth={auth} />;
}

function SignedOut({ auth }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const start = async (provider) => {
    setBusy(provider);
    setError(null);
    try {
      // Resolves only if the redirect never happens, so `busy` staying set is
      // correct on the success path — the page is on its way out.
      await signInWith(provider);
    } catch (err) {
      setError(err);
      setBusy(null);
    }
  };

  return (
    <Landing>
      {/* Why the last session ended — an expiry, or a rule that refused it. */}
      {auth.message && <div className="alert warn" role="status">{auth.message}</div>}
      {error && <div className="alert error" role="alert">{error.message}</div>}

      {auth.providers.length === 0 && (
        <div className="alert error" role="alert">
          This deployment offers no sign-in providers. Set PANTRY_AUTH_PROVIDERS.
        </div>
      )}

      <div className="stack">
        {/* `email` is not an OAuth provider -- there is nowhere to redirect to.
            It gets a form, and the redirect buttons get the rest. */}
        {auth.providers.filter((p) => p !== 'email').map((provider) => (
          <button
            key={provider}
            type="button"
            className="primary full"
            disabled={Boolean(busy)}
            onClick={() => start(provider)}
          >
            {busy === provider ? <Spinner /> : null}
            {providerLabel(provider)}
          </button>
        ))}
        {auth.providers.includes('email') && (
          <EmailSignIn alone={auth.providers.every((p) => p === 'email')} />
        )}
      </div>
    </Landing>
  );
}

/** Centred single-card layout, used by every state above. */
function Screen({ title, children }) {
  return (
    <div className="signin">
      <div className="card stack">
        {title && <h1>{title}</h1>}
        {children}
      </div>
    </div>
  );
}

/**
 * Sign in with an email address.
 *
 * Rendered when 'email' is among the providers. Kept visually secondary to the
 * OAuth buttons where both exist, and the only thing on the screen where it is
 * the sole provider -- which is the state a deployment is in before anyone has
 * configured Google or Apple.
 *
 * Two steps: the address, then the numeric code the message carries. The code
 * leads because the link in that message cannot finish the job everywhere this
 * app is installed. Its host is the Supabase project, so Android opens it in
 * Chrome rather than in the app packaged for Play, and an iOS home-screen
 * install bounces out to Safari in the same way; the session is real but the
 * person is now reading the app in a browser standing beside the app. Digits
 * typed here finish on the screen they were asked on, on every platform.
 * The link stays -- it is fewer taps on a laptop, and it is the way back in
 * when a code is refused for a reason nobody on this side can see.
 */
function EmailSignIn({ alone }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState('');
  // Which request is in flight, or null. A string rather than a boolean for the
  // same reason as the provider buttons above: the code step has two buttons
  // that can be waiting, and only the one that was pressed should spin.
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [resent, setResent] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy('send');
    setError(null);
    try {
      setSent(await signInWithEmail(email));
      // A code typed against the previous address is worthless against this
      // one, and leaving it in the box would look like it had been kept.
      setCode('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    setBusy('resend');
    setError(null);
    setResent(false);
    try {
      await signInWithEmail(sent);
      setResent(true);
      // A send that went through replaced the code this address answers to, so
      // anything already in the box is now certain to be refused -- and the
      // button over it would still be lit, inviting the one tap that cannot
      // work. Same reasoning as the address step above. Only on success: a
      // send the rate limit turned away left the previous code live, and
      // clearing it there would throw away the one that still works.
      setCode('');
    } catch (err) {
      // Supabase allows one send a minute per address and says so in the error
      // it returns. Showing that sentence is what teaches somebody to wait;
      // swallowing it leaves a button that does nothing for no stated reason.
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const verify = async (event) => {
    event.preventDefault();
    setBusy('verify');
    setError(null);
    setResent(false);
    try {
      await verifyEmailCode(sent, code);
      // `busy` is deliberately left set, the same as the provider buttons: the
      // session lands, the gate renders the app in place of this form, and a
      // button that stopped spinning first would flash "ready" on its way out.
    } catch (err) {
      setError(err);
      setBusy(null);
    }
  };

  const startOver = () => {
    setSent(null);
    setCode('');
    setError(null);
    setResent(false);
  };

  // The shape verifyEmailCode insists on, applied here too so the button is
  // never enabled for something the call is going to refuse. Six to ten
  // digits, because the length is the Supabase project's setting and not
  // ours: see the comment on verifyEmailCode.
  const codeReady = /^\d{6,10}$/.test(code.replace(/\s+/g, ''));

  if (sent) {
    return (
      // Keyed so the two forms are never reconciled into one another. Both are
      // a <form> in the same position, so React lines their children up by
      // index, and a field that lands on an index the other form also filled is
      // handed the existing DOM node -- while `autoFocus` fires only on a real
      // mount. The current indices happen not to collide; the key is what stops
      // one more line of markup from quietly making them.
      <form key="code" className="stack" onSubmit={verify}>
        <div className="alert ok" role="status">
          <strong>Check your email.</strong>
          <div className="small" style={{ marginTop: 4 }}>
            We sent a code to {sent}. Enter it here. The same email has a link,
            which is quicker on a computer.
          </div>
        </div>
        {error && <div className="alert error" role="alert">{error.message}</div>}
        {/* Silence after tapping "Send a new code" reads as a broken button,
            and this is the one screen with no Announcer over it to say so. */}
        {resent && (
          <div className="small muted" role="status">
            A new code is on its way. Use the one in the newest email.
          </div>
        )}
        {/* Focused on arrival: whoever is here is holding the phone that is
            about to buzz. `one-time-code` is what offers the digits above the
            keyboard on both platforms, and `inputMode` makes it a number pad.
            No `mono` class -- it sets 0.85em, which would drop the field under
            the 16px floor the input rule keeps to stop iOS zooming on focus.

            Ten characters is the cap -- the longest code Supabase can be set to
            send -- and it is applied on the way in rather than with
            `maxLength`. The browser trims an over-long insertion before React
            is handed the value, so a code pasted with a leading or interior
            space arrived already a digit short: the space that both this
            screen and `verifyEmailCode` mean to forgive had taken a digit's
            place instead of being dropped, and the box then held a full-looking
            code over a button that would not light up. No fixed-length
            placeholder either: the project decides how many digits, and a
            box showing six to somebody holding eight reads as broken. */}
        <input
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\s+/g, '').slice(0, 10))}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          placeholder="Code from the email"
          aria-label="Sign-in code from the email"
          autoFocus
        />
        <button type="submit" className="primary full" disabled={Boolean(busy) || !codeReady}>
          {busy === 'verify' ? <Spinner /> : null}
          Sign in
        </button>
        {/* Both are held while anything is in flight. An answer that arrives
            after the address on screen has changed would report itself against
            the wrong one. */}
        <div className="row wrap">
          <button type="button" className="link" disabled={Boolean(busy)} onClick={resend}>
            {busy === 'resend' ? <Spinner /> : null}
            Send a new code
          </button>
          <button type="button" className="link" disabled={Boolean(busy)} onClick={startOver}>
            Use a different address
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="stack" onSubmit={submit}>
      {!alone && <div className="signin-or"><span>or</span></div>}
      {error && <div className="alert error" role="alert">{error.message}</div>}
      <input
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        required
        aria-label="Email address"
      />
      <button type="submit" className={alone ? 'primary full' : 'full'} disabled={Boolean(busy) || !email.trim()}>
        {busy === 'send' ? <Spinner /> : null}
        Email me a sign-in code
      </button>
      <p className="tiny muted" style={{ margin: 0 }}>
        No password. We email you a code that works once.
      </p>
    </form>
  );
}
